import { useState } from "react";
import { useTranslate } from "@refinedev/core";

import { uploadNazorgFoto, type NazorgAntwoord, type NazorgVraag } from "./nazorgApi";

// The adopter-facing answer control for one question — the design handoff's shared "Vraag" component.
// Each NazorgVraag.type maps to one touch-friendly control; the question text + helper live in the page
// (this is the answer input only). Value + onChange are owned by the page; this is presentation.

export interface VraagControlProps {
  vraag: NazorgVraag;
  value: NazorgAntwoord;
  onChange: (value: NazorgAntwoord) => void;
  /** The invitation token — needed by the photo control to upload against the portal endpoint. */
  token: string;
}

/** True when a question has a usable answer — drives the section "ingevuld" counts + the progress bar. */
export function isAnswered(vraag: NazorgVraag, value: NazorgAntwoord): boolean {
  if (vraag.type === "multi") {
    return Array.isArray(value) && value.length > 0;
  }
  if (vraag.type === "scale") {
    return typeof value === "number" && value >= 1;
  }
  return typeof value === "string" && value.trim() !== "";
}

/** 5-point Likert: a row of 5 equal columns, each a dot above a label. */
function ScaleControl({ vraag, value, onChange }: VraagControlProps) {
  const levels = vraag.levels ?? [];
  return (
    <div className="nazorg-scale" role="radiogroup" aria-label={vraag.q}>
      {levels.map((label, i) => {
        const pos = i + 1;
        const selected = value === pos;
        return (
          <button
            key={pos}
            type="button"
            role="radio"
            aria-checked={selected}
            className={"nazorg-scale__opt" + (selected ? " is-selected" : "")}
            onClick={() => onChange(selected ? undefined : pos)}
          >
            <span className="nazorg-scale__dot" aria-hidden="true" />
            <span className="nazorg-scale__label">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** single / yesno: a vertical list of single-select buttons with a round check mark. */
function SingleControl({ vraag, value, onChange }: VraagControlProps) {
  const options = vraag.options ?? [];
  return (
    <div className="nazorg-choices" role="radiogroup" aria-label={vraag.q}>
      {options.map((opt) => {
        const selected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={selected}
            className={"nazorg-choice" + (selected ? " is-selected" : "")}
            onClick={() => onChange(selected ? undefined : opt)}
          >
            <span className="nazorg-choice__mark" aria-hidden="true">
              {selected ? "✓" : ""}
            </span>
            <span className="nazorg-choice__label">{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

/** multi: same list as single but with square check boxes and toggle (multiple) selection. */
function MultiControl({ vraag, value, onChange }: VraagControlProps) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (opt: string) =>
    onChange(selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt]);
  return (
    <div className="nazorg-choices" role="group" aria-label={vraag.q}>
      {(vraag.options ?? []).map((opt) => {
        const isOn = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={isOn}
            className={"nazorg-choice" + (isOn ? " is-selected" : "")}
            onClick={() => toggle(opt)}
          >
            <span className="nazorg-choice__mark nazorg-choice__mark--multi" aria-hidden="true">
              {isOn ? "✓" : ""}
            </span>
            <span className="nazorg-choice__label">{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

/** photo: upload one image and keep its URL as the answer; shows a preview with replace/remove. */
function PhotoControl({ value, onChange, token }: VraagControlProps) {
  const t = useTranslate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const url = typeof value === "string" ? value : "";

  async function upload(file: File | undefined) {
    if (!file) {
      return;
    }
    setError(undefined);
    setBusy(true);
    try {
      onChange(await uploadNazorgFoto(token, file));
    } catch {
      setError(t("nazorg.foto.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="nazorg-foto">
      {url ? (
        <img className="nazorg-foto__preview" src={url} alt={t("nazorg.foto.previewAlt")} />
      ) : null}
      <label className="nazorg-foto__button">
        <input
          type="file"
          accept="image/jpeg,image/png"
          className="nazorg-foto__input"
          disabled={busy}
          onChange={(e) => void upload(e.target.files?.[0])}
        />
        {busy ? t("nazorg.foto.uploading") : url ? t("nazorg.foto.replace") : t("nazorg.foto.choose")}
      </label>
      {url && !busy ? (
        <button type="button" className="nazorg-foto__remove" onClick={() => onChange(undefined)}>
          {t("nazorg.foto.remove")}
        </button>
      ) : null}
      {error ? (
        <p className="nazorg-foto__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function VraagControl({ vraag, value, onChange, token }: VraagControlProps) {
  const text = typeof value === "string" ? value : "";
  switch (vraag.type) {
    case "scale":
      return <ScaleControl vraag={vraag} value={value} onChange={onChange} token={token} />;
    case "single":
    case "yesno":
      return <SingleControl vraag={vraag} value={value} onChange={onChange} token={token} />;
    case "multi":
      return <MultiControl vraag={vraag} value={value} onChange={onChange} token={token} />;
    case "text":
      return (
        <textarea
          className="nazorg-textarea"
          rows={4}
          placeholder={vraag.placeholder}
          value={text}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "number":
      return (
        <input
          className="nazorg-number"
          type="number"
          placeholder={vraag.placeholder}
          value={text}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "date":
      return (
        <input
          className="nazorg-date"
          type="date"
          value={text}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "photo":
      return <PhotoControl vraag={vraag} value={value} onChange={onChange} token={token} />;
    default:
      return null;
  }
}
